import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { adminListSettings, adminSaveSetting } from "@/lib/admin.functions";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/settings")({
  head: () =>
    privatePageMetadata(
      "/nc/live/adm/settings",
      "Settings | Control room",
      "Internal administration.",
    ),
  component: AdminSettings,
});

const FEATURES = [
  ["marketplace", "Marketplace open to members"],
  ["collabs", "Collabs open to members"],
  ["signups", "New registrations allowed"],
  ["withdrawals", "Payout requests allowed"],
  ["topups", "Wallet top-ups allowed"],
] as const;

type Json = Record<string, unknown>;

function AdminSettings() {
  const qc = useQueryClient();
  const list = useServerFn(adminListSettings);
  const save = useServerFn(adminSaveSetting);

  const { data: rows = [] } = useQuery({
    queryKey: ["adm-settings"],
    queryFn: () => list({ data: undefined }),
  });

  const saveMut = useMutation({
    mutationFn: (input: { key: string; value: Json }) => save({ data: input }),
    onSuccess: () => {
      toast.success("Saved.");
      void qc.invalidateQueries({ queryKey: ["adm-settings"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed."),
  });

  const get = (key: string): Json => {
    const row = rows.find((r) => r.key === key);
    return (row?.value ?? {}) as Json;
  };

  const maintenance = get("maintenance");
  const announcement = get("announcement");
  const features = get("features");

  const [message, setMessage] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const maintenanceMessage =
    message ?? (typeof maintenance["message"] === "string" ? (maintenance["message"] as string) : "");
  const bannerText =
    banner ?? (typeof announcement["text"] === "string" ? (announcement["text"] as string) : "");

  return (
    <div className="space-y-6">
      <section className="card-surface space-y-4 p-4">
        <h2 className="font-display text-lg font-bold">Maintenance</h2>
        <label className="flex items-center gap-3 text-sm">
          <Switch
            checked={maintenance["enabled"] === true}
            onCheckedChange={(v) =>
              saveMut.mutate({
                key: "maintenance",
                value: { ...maintenance, enabled: v, message: maintenanceMessage },
              })
            }
          />
          Maintenance mode — public site shows a holding page (admins still get through)
        </label>
        <div className="space-y-1">
          <Label>Maintenance message</Label>
          <Textarea rows={3} value={maintenanceMessage} onChange={(e) => setMessage(e.target.value)} />
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() =>
              saveMut.mutate({
                key: "maintenance",
                value: { ...maintenance, message: maintenanceMessage },
              })
            }
          >
            Save message
          </Button>
        </div>
      </section>

      <section className="card-surface space-y-3 p-4">
        <h2 className="font-display text-lg font-bold">Site announcement</h2>
        <Input
          value={bannerText}
          placeholder="Shown as a banner across the site (empty = hidden)"
          onChange={(e) => setBanner(e.target.value)}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => saveMut.mutate({ key: "announcement", value: { text: bannerText } })}
        >
          Save announcement
        </Button>
      </section>

      <section className="card-surface space-y-4 p-4">
        <h2 className="font-display text-lg font-bold">Feature switches</h2>
        {FEATURES.map(([key, label]) => (
          <label key={key} className="flex items-center gap-3 text-sm">
            <Switch
              checked={features[key] !== false}
              onCheckedChange={(v) =>
                saveMut.mutate({ key: "features", value: { ...features, [key]: v } })
              }
            />
            {label}
          </label>
        ))}
      </section>

      <section className="card-surface space-y-2 p-4">
        <h2 className="font-display text-lg font-bold">Raw settings</h2>
        <div className="space-y-1 text-xs text-muted-foreground">
          {rows.map((r) => (
            <p key={r.key} className="font-mono">
              {r.key}: {JSON.stringify(r.value)}
            </p>
          ))}
        </div>
      </section>
    </div>
  );
}
